import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Activity, BarChart3, Save, ExternalLink } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const API = `${getBackendUrl()}/api`;

export const TrackingManager = () => {
  const [settings, setSettings] = useState({
    meta_pixel_id: "",
    ga4_measurement_id: "",
    meta_pixel_enabled: false,
    ga4_enabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/tracking`);
      setSettings(res.data);
      setLoaded(true);
    } catch (err) {
      console.error("Erreur chargement tracking settings", err);
      setLoaded(true);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/settings/tracking`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Paramètres de tracking sauvegardés");
    } catch (err) {
      console.error("Erreur sauvegarde tracking", err);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Marketing & Tracking</h2>
        <p className="text-gray-500 mt-1">
          Configurez vos outils de suivi pour mesurer les performances de vos campagnes publicitaires.
        </p>
      </div>

      {/* Meta Pixel */}
      <Card className="border-2" data-testid="meta-pixel-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 008.44-9.9c0-5.53-4.5-10.02-10-10.02z" />
                </svg>
              </div>
              <div>
                <CardTitle className="text-lg">Meta Pixel (Facebook)</CardTitle>
                <CardDescription>Suivez les conversions de vos campagnes Meta/Instagram</CardDescription>
              </div>
            </div>
            <Switch
              data-testid="meta-pixel-toggle"
              checked={settings.meta_pixel_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, meta_pixel_enabled: checked })
              }
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="meta-pixel-id">ID du Pixel Meta</Label>
            <Input
              id="meta-pixel-id"
              data-testid="meta-pixel-id-input"
              placeholder="Ex: 123456789012345"
              value={settings.meta_pixel_id}
              onChange={(e) =>
                setSettings({ ...settings, meta_pixel_id: e.target.value })
              }
              className="mt-1 font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              Trouvez votre ID Pixel dans le{" "}
              <a
                href="https://business.facebook.com/events_manager"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline inline-flex items-center gap-1"
              >
                Meta Events Manager <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>

          {settings.meta_pixel_enabled && settings.meta_pixel_id && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">
                Pixel actif sur les pages <code className="bg-green-100 px-1 rounded">/landing</code> et <code className="bg-green-100 px-1 rounded">/merci</code>
              </span>
            </div>
          )}

          {settings.meta_pixel_enabled && !settings.meta_pixel_id && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-700">
                Activez le Pixel et entrez votre ID pour commencer le suivi
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Analytics 4 */}
      <Card className="border-2" data-testid="ga4-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Google Analytics 4</CardTitle>
                <CardDescription>Analysez le trafic et le comportement des visiteurs</CardDescription>
              </div>
            </div>
            <Switch
              data-testid="ga4-toggle"
              checked={settings.ga4_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, ga4_enabled: checked })
              }
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="ga4-id">ID de mesure GA4</Label>
            <Input
              id="ga4-id"
              data-testid="ga4-id-input"
              placeholder="Ex: G-XXXXXXXXXX"
              value={settings.ga4_measurement_id}
              onChange={(e) =>
                setSettings({ ...settings, ga4_measurement_id: e.target.value })
              }
              className="mt-1 font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              Trouvez votre ID dans{" "}
              <a
                href="https://analytics.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline inline-flex items-center gap-1"
              >
                Google Analytics <ExternalLink className="w-3 h-3" />
              </a>
              {" > Admin > Flux de données"}
            </p>
          </div>

          {settings.ga4_enabled && settings.ga4_measurement_id && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">
                GA4 actif sur toutes les pages du site
              </span>
            </div>
          )}

          {settings.ga4_enabled && !settings.ga4_measurement_id && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-700">
                Activez GA4 et entrez votre ID de mesure pour commencer le suivi
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="border border-gray-200 bg-gray-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Comment obtenir vos identifiants ?</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-500 mt-0.5">1.</span>
              <span>
                <strong>Meta Pixel :</strong> Connectez-vous au{" "}
                <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                  Meta Business Manager
                </a>
                , allez dans Gestionnaire d'événements et copiez votre ID Pixel.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-500 mt-0.5">2.</span>
              <span>
                <strong>Google Analytics 4 :</strong> Connectez-vous à{" "}
                <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                  Google Analytics
                </a>
                , allez dans Admin &gt; Flux de données &gt; Web et copiez votre ID de mesure (G-XXXXXXXXXX).
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          data-testid="save-tracking-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 px-8"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Sauvegarde..." : "Sauvegarder les paramètres"}
        </Button>
      </div>
    </div>
  );
};
